import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, useTheme } from '@mui/material';

import { loginSuccess } from '../reducers/authReducer';
import { setLoading } from '../reducers/loadingReducer';
import { login } from '../services/apiService';

import AbstractBackground from '../components/AbstractBackground';
import HeroCard from '../components/HeroCard';

const Login = () => {
  const [error, setError] = useState('');
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const loading = useSelector((state) => state.loading);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const theme = useTheme(); // Access current theme

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const showRegistration = () => {
    navigate('/register');
  };

  const handleLogin = async (email, password) => {
    dispatch(setLoading(true));
    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      const data = await login(email, password, dispatch);

      localStorage.setItem('token', data.token);
      navigate('/');
      dispatch(loginSuccess({ user: data.user, token: data.token }));
      setError(null);
    } catch (error) {
      console.error('Failed to login:', error);
      setError(error.message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const memoizedBackground = useMemo(
    () => <AbstractBackground animate={loading} containerRef={containerRef} />,
    [loading]
  );

  return (
    <Box
      sx={{
        position: 'absolute', // anchors absolute background
        width: '100%',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {memoizedBackground}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          m: 2,
          p: 2,
          borderRadius: 2,
          boxShadow: 2,
          animation: 'fadeIn 0.25s ease-in-out',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(to top, rgba(33,33,33,0.9), rgba(25,118,210,0.3))'
            : 'linear-gradient(to top, rgba(255,255,255,0.8), rgba(0,116,211,0.1))',
          '@keyframes fadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      >
        <HeroCard
          handleLogin={handleLogin}
          showRegister={showRegistration}
          error={error}
        />
      </Box>
    </Box>
  );
};

export default Login;
